<template>
  <div class="md:max-w-screen-xl flex flex-col relative md:py-3 md:mx-auto">
    <div class="flex md:hidden py-3 px-3 w-full">
      <DropDown
        :options="sectionOptions"
        @select="selectSection"
        :initial="{ label: 'Summary', value: 'summary' }"
        :showSelectedAsGray="false"
      ></DropDown>
    </div>

    <template v-if="getCurrentApplication">
      <!-- START DESKTOP HEADER -->
      <div class="rounded-lg bg-white px-5 py-5 shadow-sm hidden md:flex flex-row">
        <div class="flex flex-col w-1/5">
          <h5 class="text-2xl py-1">
            {{ getCurrentApplication.applicant.name }}
          </h5>
          <p class="text-sm py-1">
            {{ getCurrentApplication.unit_address }}
          </p>
        </div>

        <div class="flex flex-row items-center w-1/5 justify-between mx-2 lg:mx-8">
          <a href="https://s3.amazonaws.com/rent-front-static/sample_reports.zip">
            <DownloadIcon class="text-brand-dark-gray w-8 h-8 cursor-pointer" />
          </a>

          <PaperPlaneIcon
            :classes="'text-brand-dark-gray w-8 h-8 cursor-pointer hover:text-brand-green'"
          />
          <ClockIcon class="text-brand-dark-gray w-8 h-8 cursor-pointer hover:text-brand-green" />
          <DocumentTextIcon
            :class="[
              'w-8 h-8 cursor-pointer',
              getCurrentApplication.notes ? 'text-brand-green' : 'text-brand-dark-gray'
            ]"
          />
        </div>

        <div class="flex flex-row items-center w-1/2 px-5">
          <div v-if="getCurrentApplication.status === 'submitted'" class="flex flex-row w-full">
            <div class="mr-3">
              <SecondaryButton :classes="'border-brand-red'"
                ><p class="font-normal text-sm lg:text-base">Decline</p></SecondaryButton
              >
            </div>

            <div class="">
              <PrimaryButton
                ><p class="font-normal text-white text-xs lg:text-sm lg:py-0.5">
                  Select as tenant
                </p></PrimaryButton
              >
            </div>
          </div>
        </div>

        <div class="flex flex-row items-center border border-brand-mid-gray mx-2 lg:mx-5"></div>

        <div class="flex flex-row items-center lg:px-5">
          <SecondaryButton><p class="font-normal text-sm lg:text-base">Archive</p></SecondaryButton>
        </div>
      </div>
      <!-- END DESKTOP HEADER -->
    </template>

    <div class="flex flex-row">
      <!-- START DESKTOP SIDE NAV -->
      <div class="hidden md:flex flex-col w-1/5 py-5 pr-5">
        <router-link
          :to="{
            name: 'home_demo_summary'
          }"
          class="py-2"
        >
          <button
            class="w-full py-3 px-5 rounded-lg focus:outline-none text-base text-left"
            :class="[
              $route.name === 'home_demo_summary'
                ? 'text-brand-green border-2 border-brand-green bg-brand-background-light-green'
                : 'text-brand-black bg-white'
            ]"
          >
            Summary
          </button>
        </router-link>

        <router-link
          :to="{
            name: 'home_demo_application'
          }"
          class="py-2"
        >
          <button
            class="w-full py-3 px-5 rounded-lg focus:outline-none text-base text-left"
            :class="[
              $route.name === 'home_demo_application'
                ? 'text-brand-green border-2 border-brand-green bg-brand-background-light-green'
                : 'text-brand-black bg-white'
            ]"
          >
            Application
          </button>
        </router-link>

        <template v-if="getCurrentApplication">
          <router-link
            :to="{
              name: 'home_demo_report',
              params: {
                type: profile.report_type
              }
            }"
            v-for="profile in getCurrentApplication.reports"
            :key="profile.id"
            class="py-2 flex flex-row"
          >
            <button
              class="w-full py-3 px-5 rounded-lg focus:outline-none text-base text-left"
              :class="[
                $route.name === 'home_demo_report' && $route.params.type === profile.report_type
                  ? 'border-2 border-brand-green bg-brand-background-light-green'
                  : 'bg-white'
              ]"
            >
              <div class="flex">
                <p
                  :class="[
                    $route.params.type === profile.report_type
                      ? 'text-brand-green'
                      : pendingStatuses.includes(profile.status)
                      ? 'text-brand-mid-gray'
                      : 'text-brand-black'
                  ]"
                >
                  {{ profile.short_label }}
                </p>
                <div
                  class="flex items-center ml-1"
                  v-if="profile.status.includes('user_action_required')"
                >
                  <ExclamationIcon class="text-brand-yellow w-5 h-5" />
                </div>
                <div class="flex items-center ml-1" v-if="pendingStatuses.includes(profile.status)">
                  <ClockIcon class="text-brand-mid-gray w-5 h-5" />
                </div>

                <div class="flex items-center ml-1" v-if="errorStatuses.includes(profile.status)">
                  <XIcon class="text-brand-red w-5 h-5" />
                </div>
              </div>
            </button>
          </router-link>
        </template>
      </div>
      <!-- END DESKTOP SIDE NAV -->

      <div class="flex w-full md:w-4/5 py-5 bg-brand-light-gray">
        <router-view
          :key="`${$route.name}-sample${$route.params.type ? `-${$route.params.type}` : ''}`"
        />
      </div>
    </div>
  </div>
</template>
<script>
import {
  DownloadIcon,
  DocumentTextIcon,
  ClockIcon,
  ExclamationIcon,
  XIcon
} from "@heroicons/vue/outline"

import { mapGetters } from "vuex"

import { APPLICATION_GET } from "@/store/types"

export default {
  components: {
    DownloadIcon,
    DocumentTextIcon,
    ClockIcon,
    ExclamationIcon,
    XIcon
  },
  computed: {
    ...mapGetters(["getCurrentApplication"])
  },
  data() {
    return {
      pendingStatuses: ["sent_payment_request", "new", "pending", "requested"],
      errorStatuses: ["error", "fatal"],
      sectionOptions: [
        { label: "Summary", value: "summary" },
        { label: "Application", value: "application" },
        { label: "Credit", value: "credit" },
        { label: "Eviction", value: "eviction" },
        { label: "Criminal", value: "criminal" }
      ]
    }
  },
  async mounted() {
    await this.$store.dispatch(APPLICATION_GET, { id: "sample" })

    if (this.$route.name === "home_demo") {
      this.$router.push({ name: "home_demo_summary" })
    }
  },
  methods: {
    selectSection(section) {
      if (["summary", "application"].includes(section.value)) {
        this.$router.push({
          name: `home_demo_${section.value}`
        })
      } else {
        this.$router.push({
          name: "home_demo_report",
          params: { type: section.value }
        })
      }
    }
  }
}
</script>
